// src/services/queryMonitor.js

/**
 * Query Monitor for TimeMate Admin
 * Monitors Firestore queries for budget enforcement and performance tracking
 *
 * Budget targets (from PLAN.md Section 4.5):
 * - Per session budget: ~500 reads max
 * - Per page budget: ~100 reads max
 * - Warning threshold: >100 docs per query
 * - Error threshold: >1000 docs per query
 */

const QUERY_BUDGET = {
  WARNING_THRESHOLD: 100,   // Warn if query returns >100 docs
  ERROR_THRESHOLD: 1000,    // Error if query returns >1000 docs
  SESSION_BUDGET: 500,      // Max reads per session (target)
  PAGE_BUDGET: 100,         // Max reads per page (target)
};

class QueryMonitor {
  constructor() {
    /** @type {number} Total reads in current session */
    this.sessionReads = 0;

    /** @type {Array<Object>} Log of all queries */
    this.queryLog = [];

    /** @type {number} Session start timestamp */
    this.sessionStart = Date.now();

    /** @type {boolean} Whether to log to console */
    this.verbose = process.env.NODE_ENV === 'development';
  }

  /**
   * Log and monitor a query result
   * @param {string} queryName - Identifier for the query
   * @param {number} resultCount - Number of documents returned
   * @param {Object} metadata - Additional query info
   * @returns {Object} Log entry
   */
  logQuery(queryName, resultCount, metadata = {}) {
    this.sessionReads += resultCount;

    const logEntry = {
      timestamp: Date.now(),
      queryName,
      resultCount,
      sessionTotal: this.sessionReads,
      ...metadata
    };

    this.queryLog.push(logEntry);

    // Thresholds checked silently - use getStats() to review

    return logEntry;
  }

  /**
   * Log query with timing information
   * @param {string} queryName - Identifier for the query
   * @param {number} startTime - Start timestamp (Date.now())
   * @param {number} resultCount - Number of documents returned
   * @returns {Object} Log entry with duration
   */
  logQueryMetrics(queryName, startTime, resultCount) {
    const duration = Date.now() - startTime;
    return this.logQuery(queryName, resultCount, { duration });
  }

  /**
   * Get session statistics
   * @returns {Object} Statistics about current session
   */
  getStats() {
    const sessionDuration = (Date.now() - this.sessionStart) / 1000; // seconds
    const readsPerMinute = sessionDuration > 0
      ? this.sessionReads / (sessionDuration / 60)
      : 0;

    return {
      totalReads: this.sessionReads,
      queryCount: this.queryLog.length,
      sessionDurationSeconds: Math.round(sessionDuration),
      readsPerMinute: Math.round(readsPerMinute * 10) / 10,
      budgetUsed: `${((this.sessionReads / QUERY_BUDGET.SESSION_BUDGET) * 100).toFixed(1)}%`,
      topQueries: this.getTopQueries(),
      budgets: QUERY_BUDGET,
    };
  }

  /**
   * Get queries sorted by read count
   * @returns {Array} Top queries by total reads
   */
  getTopQueries() {
    const queryTotals = {};
    this.queryLog.forEach(entry => {
      if (!queryTotals[entry.queryName]) {
        queryTotals[entry.queryName] = { count: 0, reads: 0, avgDuration: 0 };
      }
      queryTotals[entry.queryName].count++;
      queryTotals[entry.queryName].reads += entry.resultCount;
      if (entry.duration) {
        queryTotals[entry.queryName].avgDuration += entry.duration;
      }
    });

    // Calculate averages
    Object.keys(queryTotals).forEach(key => {
      if (queryTotals[key].count > 0) {
        queryTotals[key].avgDuration = Math.round(
          queryTotals[key].avgDuration / queryTotals[key].count
        );
      }
    });

    return Object.entries(queryTotals)
      .sort((a, b) => b[1].reads - a[1].reads)
      .slice(0, 10)
      .map(([name, stats]) => ({ name, ...stats }));
  }

  /**
   * Reset session tracking
   * Call this on login or when starting a new session
   */
  resetSession() {
    this.sessionReads = 0;
    this.queryLog = [];
    this.sessionStart = Date.now();
  }

  /**
   * Create a wrapped query function that auto-logs
   * @param {string} queryName - Name for logging
   * @param {Function} queryFn - The query function to wrap
   * @returns {Function} Wrapped function that logs query metrics
   */
  wrapQuery(queryName, queryFn) {
    return async (...args) => {
      const startTime = Date.now();
      const result = await queryFn(...args);

      // Determine count from various result types
      const count = Array.isArray(result)
        ? result.length
        : result?.docs
          ? result.docs.length
          : result?.size ?? 1;

      this.logQueryMetrics(queryName, startTime, count);
      return result;
    };
  }

  /**
   * Enable/disable verbose logging
   * @param {boolean} enabled - Whether to enable verbose mode
   */
  setVerbose(enabled) {
    this.verbose = enabled;
  }

  /**
   * Get stats for debugging (call getStats() instead of printStats in production)
   * This method is kept for development use only
   */
  printStats() {
    // Stats logging disabled - use getStats() to retrieve statistics programmatically
    return this.getStats();
  }
}

// Create singleton instance
export const queryMonitor = new QueryMonitor();

// Convenience functions for direct import
export function monitorQuery(queryName, resultCount, metadata) {
  return queryMonitor.logQuery(queryName, resultCount, metadata);
}

export function logQueryMetrics(queryName, startTime, resultCount) {
  return queryMonitor.logQueryMetrics(queryName, startTime, resultCount);
}

export default queryMonitor;
